import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import Select from "react-select";
import { toast } from "react-toastify";
import { getUserAvatarColor } from "../../../utils/kpiColors";

const ProjectManageMembersModal = ({ project, onClose }) => {
  const dispatch = useDispatch();
  const projectId = project?._id || project?.project_id || project?.id;

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const [allAvailableUsers, setAllAvailableUsers] = useState([]);
  const [invitedMember, setInvitedMember] = useState(null);
  const [inviting, setInviting] = useState(false);

  // Fetch current members of the project
  const loadProjectMembers = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const res = await dispatch.projectDetails.getMembersList({ project_id: projectId });
      const rawList = res?.data || (Array.isArray(res) ? res : []);
      setMembers(Array.isArray(rawList) ? rawList : []);
    } catch (err) {
      console.error("Error fetching project members:", err);
      setMembers([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all company members for invite dropdown using listAllMembers
  const loadAllCompanyMembers = async () => {
    try {
      const res = await dispatch.people.listAllMembers();
      const raw = res?.data || res?.input || (Array.isArray(res) ? res : []);
      setAllAvailableUsers(Array.isArray(raw) ? raw : []);
    } catch (err) {
      console.error("Error fetching company members:", err);
    }
  };

  useEffect(() => {
    loadProjectMembers();
    loadAllCompanyMembers();
  }, [projectId]);

  // Handle member addition matching original Member.js implementation
  const handleInviteMember = async () => {
    if (!invitedMember || !invitedMember.value || inviting) {
      toast.error("Please select a member to add");
      return;
    }

    setInviting(true);
    try {
      const payload = [
        {
          user_id: invitedMember.value,
          project_id: projectId,
          status: "active",
          invitation: true,
        },
      ];
      const responseData = await dispatch.projects.inviteMember(payload);
      await loadProjectMembers();
      setInviting(false);

      if (responseData?.data?.success || responseData?.success) {
        toast.success("Member added successfully");
        setInvitedMember(null);
      } else {
        toast.error(responseData?.data?.message || responseData?.message || "Failed to add member");
      }
    } catch (err) {
      toast.error(err?.error || err?.message || "Failed to add member");
      setInviting(false);
    }
  };

  // Handle member removal / status update
  const handleRemoveMember = async (userId) => {
    if (!userId) return;
    try {
      await dispatch.projects.inviteMember([
        {
          user_id: userId,
          project_id: projectId,
          status: "deleted",
        },
      ]);
      toast.success("Member removed");
      await loadProjectMembers();
    } catch (err) {
      toast.error(err?.error || "Failed to remove member");
    }
  };

  // Filter members by search
  const filteredMembers = members.filter((m) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    const uObj = m.user_id || m.user || m;
    const name = `${uObj.first_name || ""} ${uObj.last_name || ""}`.trim() || uObj.name || uObj.full_name || "";
    const email = uObj.email || "";
    return name.toLowerCase().includes(q) || email.toLowerCase().includes(q);
  });

  // Exclude members already in project from invite options
  const existingUserIds = new Set(
    members.map((m) => String(m.user_id?._id || m.user_id?.id || m.user_id || m._id || m.id))
  );

  const inviteOptions = allAvailableUsers
    .filter((u) => !existingUserIds.has(String(u._id || u.id)))
    .map((u) => {
      const name = `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.name || u.email;
      const desig = typeof u.designation === "object" ? u.designation?.name : u.designation;
      return {
        label: desig ? `${name} (${desig})` : name,
        value: u._id || u.id,
      };
    });

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      borderRadius: "0.75rem",
      borderColor: state.isFocused ? "#0f172a" : "#e2e8f0",
      boxShadow: state.isFocused ? "0 0 0 2px rgba(15, 23, 42, 0.08)" : "none",
      fontSize: "0.8125rem",
      padding: "2px 4px",
      backgroundColor: "#ffffff",
      "&:hover": {
        borderColor: "#cbd5e1",
      },
    }),
  };

  return (
    <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-xs z-[200] flex items-center justify-center p-4 animate-fadeIn">
      <div className="bg-white w-full max-w-lg rounded-3xl p-6 sm:p-7 shadow-2xl z-50 flex flex-col max-h-[85vh] border border-slate-100">
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b border-slate-100 mb-5">
          <div>
            <h2 className="text-lg font-bold text-slate-900 truncate">
              {project?.name || "Project"} Members
            </h2>
            <p className="text-xs text-slate-400 font-medium mt-0.5">
              {members.length} team member{members.length === 1 ? "" : "s"} assigned
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center transition-colors font-bold text-sm"
          >
            ✕
          </button>
        </div>

        {/* Cohesive Invite Bar */}
        <div className="p-4 bg-slate-50 border border-slate-200/80 rounded-2xl mb-5">
          <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-400 font-mono mb-2">
            Add New Member
          </label>
          <div className="flex flex-col sm:flex-row gap-2.5 items-stretch sm:items-center">
            <div className="flex-1 w-full">
              <Select
                styles={selectStyles}
                value={invitedMember}
                isSearchable={true}
                isClearable={true}
                options={inviteOptions}
                placeholder="Select team member to add..."
                onChange={(e) => setInvitedMember(e)}
              />
            </div>
            <button
              onClick={handleInviteMember}
              disabled={!invitedMember || inviting}
              className="px-4 py-2.5 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white rounded-xl text-xs font-bold transition-all shrink-0 flex items-center justify-center gap-1.5 shadow-2xs"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              <span>{inviting ? "Adding..." : "Add Member"}</span>
            </button>
          </div>
        </div>

        {/* Assigned Team Members Header & Filter */}
        <div className="flex items-center justify-between gap-3 mb-3 pb-2 border-b border-slate-100">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 font-mono">
            Assigned Team ({filteredMembers.length})
          </span>
          {members.length > 3 && (
            <div className="relative w-44">
              <input
                type="text"
                placeholder="Filter members..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-3 py-1 border border-slate-200 rounded-lg text-[11px] focus:outline-none focus:border-slate-900 bg-white"
              />
            </div>
          )}
        </div>

        {/* Member List */}
        <div className="flex-1 overflow-y-auto pr-1 divide-y divide-slate-100">
          {loading ? (
            <div className="py-12 text-center text-xs text-slate-400 font-medium">
              Loading team members...
            </div>
          ) : filteredMembers.length === 0 ? (
            <div className="py-12 text-center text-xs text-slate-400 font-medium">
              {searchQuery ? "No matching team members found." : "No team members assigned yet."}
            </div>
          ) : (
            filteredMembers.map((item, idx) => {
              const uObj = item.user_id || item.user || item;
              const firstName = uObj.first_name || "";
              const lastName = uObj.last_name || "";
              const fullName = `${firstName} ${lastName}`.trim() || uObj.name || uObj.full_name || uObj.email || "Team Member";
              const email = uObj.email || "";

              const desig = typeof uObj.designation === "object"
                ? (uObj.designation?.name || uObj.designation?.designation_name)
                : (uObj.designation || uObj.role?.name || uObj.role || "");

              const parts = fullName.split(" ").filter(Boolean);
              const initials = parts.length >= 2
                ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
                : (fullName.slice(0, 2) || "TM").toUpperCase();

              const avatarBg = getUserAvatarColor(email || fullName, idx);

              return (
                <div key={item._id || item.id || idx} className="py-3 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-10 h-10 rounded-full ${avatarBg} text-white flex items-center justify-center text-xs font-bold shrink-0 shadow-2xs`}>
                      {initials}
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs font-bold text-slate-900 truncate">{fullName}</div>
                      {email && <div className="text-[11px] text-slate-400 truncate">{email}</div>}
                      {desig && (
                        <span className="inline-block text-[10px] font-semibold text-slate-500 bg-slate-100 rounded-md px-1.5 py-0.5 mt-0.5 truncate">
                          {desig}
                        </span>
                      )}
                    </div>
                  </div>

                  <button
                    onClick={() => handleRemoveMember(uObj._id || uObj.id)}
                    className="px-3 py-1.5 text-xs font-bold text-rose-600 hover:bg-rose-50 rounded-xl transition-colors shrink-0"
                    title="Remove member"
                  >
                    Remove
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectManageMembersModal;
